import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Users, Clock, AlertTriangle, CheckCircle, XCircle, Shield, Loader2, Building2, Filter, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { API } from '../../config/apiBase';

const CHART_TOOLTIP_STYLE = { contentStyle: { background: '#0D2C63', border: '1px solid rgba(140,180,255,0.30)', borderRadius: 12, fontSize: 12, color: '#E5E5E5', padding: '10px 14px' } };
const PERIODS = [{ key: '7d', label: '7 dias' }, { key: '14d', label: '14 dias' }, { key: '30d', label: '30 dias' }];

export default function EmpresaPerformancePage() {
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [registros, setRegistros] = useState([]);
    const [allRegistros, setAllRegistros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [unidades, setUnidades] = useState([]);
    const [selectedUnidade, setSelectedUnidade] = useState('all');
    const [userUnidadeMap, setUserUnidadeMap] = useState({});
    const [period, setPeriod] = useState('30d');

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const h = { 'Authorization': `Bearer ${token}` };

    const fetchAnalytics = useCallback(async () => {
        try {
            const res = await fetch(`${API}/ponto/empresa/analytics?period=${period}`, { headers: h });
            if (res.ok) setAnalytics(await res.json());
        } catch {}
    }, [period]);

    useEffect(() => {
        if (!token) return;
        Promise.all([
            fetch(`${API}/ponto/dashboard/extended`, { headers: h }).then(r => r.json()),
            fetch(`${API}/ponto/registros`, { headers: h }).then(r => r.json()),
            fetch(`${API}/ponto/unidades`, { headers: h }).then(r => r.ok ? r.json() : []),
            fetch(`${API}/client/users`, { headers: h }).then(r => r.ok ? r.json() : []),
        ]).then(([s, reg, units, users]) => {
            setStats(s);
            const regs = Array.isArray(reg) ? reg : [];
            setAllRegistros(regs); setRegistros(regs);
            setUnidades(Array.isArray(units) ? units : []);
            const map = {};
            if (Array.isArray(users)) users.forEach(u => { if (u.unidade_id) map[u.user_id] = u.unidade_id; });
            setUserUnidadeMap(map);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

    useEffect(() => {
        if (selectedUnidade === 'all') setRegistros(allRegistros);
        else if (selectedUnidade === 'none') setRegistros(allRegistros.filter(r => !userUnidadeMap[r.user_id]));
        else setRegistros(allRegistros.filter(r => userUnidadeMap[r.user_id] === selectedUnidade));
    }, [selectedUnidade, allRegistros, userUnidadeMap]);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    const presRate = stats?.total_employees > 0 ? Math.round((stats.present_today / stats.total_employees) * 100) : 0;
    const totalRegs = registros.length;
    const entradas = registros.filter(r => r.tipo === 'entrada').length;
    const saidas = registros.filter(r => r.tipo === 'saída').length;
    const kpis = analytics?.kpis || {};
    const timeline = (analytics?.timeline || []).map(t => ({ ...t, label: t.date?.substring(5) || '' }));

    return (
        <div className="space-y-5" data-testid="empresa-performance">
            {/* Period + Unidade Filter */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    {PERIODS.map(p => (
                        <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`emp-period-${p.key}`}
                            className="px-3.5 py-2 rounded-lg text-xs font-medium transition-all"
                            style={{ background: period === p.key ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface)', border: `1px solid ${period === p.key ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: period === p.key ? 'var(--sv-accent)' : 'var(--muted)' }}>
                            {p.label}
                        </button>
                    ))}
                </div>
                {unidades.length > 0 && (
                    <div className="flex items-center gap-2">
                        <Filter size={12} style={{ color: 'var(--muted)' }} />
                        <select value={selectedUnidade} onChange={e => setSelectedUnidade(e.target.value)}
                            className="text-xs py-1.5 px-2.5 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}
                            data-testid="performance-unidade-filter">
                            <option value="all">Todas unidades</option>
                            {unidades.map(u => <option key={u.unidade_id} value={u.unidade_id}>{u.name}</option>)}
                            <option value="none">Sem unidade</option>
                        </select>
                    </div>
                )}
            </div>

            {/* KPI Row with trends */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {[
                    { label: 'Presenca Hoje', value: `${presRate}%`, icon: TrendingUp, color: presRate >= 70 ? '#10B981' : '#F59E0B', sub: `${stats?.present_today || 0} de ${stats?.total_employees || 0}` },
                    { label: 'Registros', value: kpis.total_registros || totalRegs, icon: Clock, color: 'var(--sv-gold-primary)', sub: `${kpis.trend_pct > 0 ? '+' : ''}${kpis.trend_pct || 0}% vs anterior`, trend: kpis.trend_pct },
                    { label: 'Colaboradores Ativos', value: kpis.active_employees || 0, icon: Users, color: '#3B82F6', sub: `de ${kpis.total_employees || 0} totais` },
                    { label: 'Media Diaria', value: kpis.avg_daily_registros || 0, icon: Calendar, color: '#8B5CF6', sub: `registros/dia` },
                    { label: 'Fora Geofence', value: kpis.geo_violations || 0, icon: Shield, color: kpis.geo_violations > 0 ? '#EF4444' : '#10B981', sub: 'no periodo' },
                ].map(k => (
                    <div key={k.label} className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${typeof k.color === 'string' && k.color.startsWith('#') ? k.color : '#7DB3FF'}12` }}>
                                <k.icon size={14} style={{ color: k.color }} />
                            </div>
                            {k.trend !== undefined && k.trend !== 0 && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full ml-auto" style={{ background: k.trend > 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: k.trend > 0 ? '#10B981' : '#EF4444' }}>
                                    {k.trend > 0 ? <TrendingUp size={10} className="inline" /> : <TrendingDown size={10} className="inline" />} {Math.abs(k.trend)}%
                                </span>
                            )}
                        </div>
                        <p className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>{k.value}</p>
                        <p className="text-[10px] font-medium" style={{ color: 'var(--text)' }}>{k.label}</p>
                        <p className="text-[9px]" style={{ color: 'var(--muted)' }}>{k.sub}</p>
                    </div>
                ))}
            </div>

            {/* Timeline Charts */}
            {timeline.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Registros por Dia</h3>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={timeline}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} width={30} />
                                <Tooltip {...CHART_TOOLTIP_STYLE} />
                                <Bar dataKey="entradas" fill="#10B981" name="Entradas" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="saidas" fill="#EF4444" name="Saidas" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>Taxa de Presenca (%)</h3>
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={timeline}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 10, fill: '#888' }} axisLine={false} tickLine={false} width={30} domain={[0, 100]} />
                                <Tooltip {...CHART_TOOLTIP_STYLE} />
                                <Line type="monotone" dataKey="presence_rate" stroke="#4E8DF0" strokeWidth={2} dot={{ r: 3 }} name="Presenca %" />
                                <Line type="monotone" dataKey="active_users" stroke="#3B82F6" strokeWidth={2} dot={false} name="Usuarios ativos" />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Indicadores Detalhados */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Ponto Digital</h3>
                    <div className="space-y-2">
                        {[
                            { label: 'Total de entradas', value: entradas, icon: CheckCircle, color: '#10B981' },
                            { label: 'Total de saidas', value: saidas, icon: XCircle, color: '#EF4444' },
                            { label: 'Marcacoes negadas hoje', value: stats?.denied_today || 0, icon: AlertTriangle, color: '#F59E0B' },
                            { label: 'Ajustes pendentes', value: kpis.pending_ajustes || stats?.pending_ajustes || 0, icon: BarChart3, color: '#8B5CF6' },
                        ].map((item) => (
                            <div key={item.label} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                <div className="flex items-center gap-2">
                                    <item.icon size={12} style={{ color: item.color }} />
                                    <span className="text-xs" style={{ color: 'var(--muted)' }}>{item.label}</span>
                                </div>
                                <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Resumo do Periodo</h3>
                    <div className="space-y-2">
                        {[
                            { label: 'Media presenca', value: `${kpis.avg_presence_rate || 0}%` },
                            { label: 'Total registros', value: kpis.total_registros || 0 },
                            { label: 'Periodo anterior', value: kpis.prev_registros || 0 },
                            { label: 'Ajustes no periodo', value: kpis.total_ajustes || 0 },
                        ].map((item) => (
                            <div key={item.label} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                <span className="text-xs" style={{ color: 'var(--muted)' }}>{item.label}</span>
                                <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Insights */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.05)', border: '1px solid rgba(var(--sv-accent-rgb), 0.15)' }}>
                <h3 className="text-xs font-semibold mb-2" style={{ color: 'var(--sv-gold-primary)' }}>Insights</h3>
                <ul className="space-y-1.5">
                    {presRate < 50 && <li className="text-xs" style={{ color: 'var(--muted)' }}>- Taxa de presenca abaixo de 50%. Verifique a equipe.</li>}
                    {(kpis.geo_violations || 0) > 0 && <li className="text-xs" style={{ color: 'var(--muted)' }}>- {kpis.geo_violations} marcacoes fora do geofence no periodo.</li>}
                    {(stats?.total_jornadas || 0) === 0 && <li className="text-xs" style={{ color: 'var(--muted)' }}>- Nenhuma jornada cadastrada. Configure em Escalas e Jornadas.</li>}
                    {kpis.trend_pct > 10 && <li className="text-xs" style={{ color: '#10B981' }}>- Aumento de {kpis.trend_pct}% nos registros vs periodo anterior.</li>}
                    {kpis.trend_pct < -10 && <li className="text-xs" style={{ color: '#EF4444' }}>- Queda de {Math.abs(kpis.trend_pct)}% nos registros vs periodo anterior.</li>}
                    {presRate >= 70 && <li className="text-xs" style={{ color: 'var(--muted)' }}>- Boa taxa de presenca. Equipe operando normalmente.</li>}
                    {totalRegs === 0 && <li className="text-xs" style={{ color: 'var(--muted)' }}>- Nenhum registro no periodo. Aguardando primeiras marcacoes.</li>}
                </ul>
            </div>
        </div>
    );
}
